import { useState } from "react"
import {
  Arrow,
  ButtonDark,
  ButtonLight,
  CaseCard,
  EyebrowLabel,
  Icon,
  IMG,
  ImageBand,
  Img,
  PostCard,
  Reveal,
  Section,
  SectionHeader,
  TextLink,
  FinalCta,
} from "../lib/shared"
import {
  BentoCard,
  CountUp,
  HeroConsole,
  HeroNetwork,
  MonitorVisual,
  PRINCIPLE_CARDS,
  SoftwareVisual,
  TrackerVisual,
  WordCycle,
  WorkflowVisual,
  type CardTone,
} from "../lib/visuals"
import { PROCESS, SERVICES, STUDIO_STATS } from "../lib/data"
import { useCollection } from "../lib/content"
import { SITE } from "../lib/site"
import {
  Seo,
  faqSchema,
  organizationSchema,
  serviceSchema,
  websiteSchema,
} from "../lib/seo"

/*
  Section order and tone. Light sections alternate paper and mist; ink marks
  the three turns in the story (the promise, the approach, the proof) and the
  close. Keep the alternation intact when adding or removing a section: two
  sections of the same tone in a row read as one long, unstructured block.
*/

/* Hero */
const HERO_WORDS = [
  "businesses",
  "institutions",
  "ministries",
  "enterprises",
  "agencies",
]

function Hero() {
  return (
    <section
      id="top"
      className="relative isolate overflow-hidden bg-ink text-white"
    >
      <div aria-hidden="true" className="absolute inset-0 -z-10">
        <div className="absolute -right-[10%] -top-[20%] h-[70%] w-[60%] animate-[aurora_16s_ease-in-out_infinite_alternate] rounded-full bg-[radial-gradient(closest-side,rgba(11,102,195,0.55),transparent)]" />
        <div className="absolute -bottom-[25%] left-[25%] h-[70%] w-[55%] animate-[aurora_21s_ease-in-out_infinite_alternate-reverse] rounded-full bg-[radial-gradient(closest-side,rgba(56,182,255,0.22),transparent)]" />
        <HeroNetwork />
        {/* Keeps the headline on calm ground while the network runs behind it. */}
        <div className="absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/40 to-transparent" />
      </div>

      <div className="mx-auto grid min-h-[100vh] max-w-[1280px] grid-cols-1 items-center gap-12 px-6 pb-20 pt-32 lg:grid-cols-12 lg:gap-10 lg:px-10 lg:pt-28">
        <div className="lg:col-span-7">
          <Reveal>
            <EyebrowLabel light>Software · Digital Infrastructure</EyebrowLabel>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="mt-6 text-[clamp(2rem,10.6vw,2.7rem)] font-black leading-[1.05] sm:text-[clamp(2.7rem,4.4vw,4.1rem)] tracking-[-0.04em]">
              <span className="sr-only">
                Intelligent systems that help businesses and institutions
                operate better.
              </span>
              {/*
                Three set lines, so the changing word never reflows the sentence.
                On a phone it is too narrow for that: the opening flows freely
                and the word takes its own line.
              */}
              <span aria-hidden="true">
                <span className="sm:block">Intelligent systems</span>{" "}
                <span className="sm:block sm:whitespace-nowrap">
                  that help <WordCycle words={HERO_WORDS} />
                </span>
                <span className="block">operate better.</span>
              </span>
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="mt-8 max-w-xl text-[17px] leading-[1.7] text-neutral-200">
              We design, build and implement digital systems that help businesses
              and institutions automate operations, manage information and deliver
              services more efficiently.
            </p>
          </Reveal>
          <Reveal delay={240}>
            <div className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-4">
              <ButtonLight to="/contact">Discuss your project</ButtonLight>
              <a
                href="#work"
                className="group inline-flex items-center gap-2 py-2 text-[14px] font-medium text-neutral-300 transition-colors hover:text-white"
              >
                See our work
                <Arrow className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </div>
          </Reveal>
        </div>
        <Reveal delay={200} className="min-w-0 lg:col-span-5">
          <HeroConsole />
        </Reveal>
      </div>
    </section>
  )
}

/* Client strip */
/*
  Logos live in public/brands/, one file per client. Each is the client's own
  artwork, trimmed of its transparent margin and exported at 160px tall — four
  times the height it renders at, and about 10 KB each. To add a brand, drop a
  transparent PNG or SVG in that folder and add a row here.

  `h` is the height that logo renders at, and it is the whole trick to making
  a strip look even.

  Capping every mark at one height does NOT look uniform, because these logos
  are not built alike. Soroman is a single-line wordmark, so at 40px tall its
  letters are 30px; The Project School stacks three lines under a shield, so at
  the same 40px its letters are 7px. Side by side the first shouts and the
  second is unreadable mush — which is exactly what a shared max-height
  produces. These numbers normalise the thing a visitor actually reads, the
  brand NAME, so Soroman sits at 30px and The Project School at 58px and they
  look like the same size on the page.

  A brand without a `logo` falls back to its name set in type, so the strip
  never breaks while artwork is still being collected.

  Ordered for rhythm, not alphabetically: the two compact marks are kept apart
  so the row does not clump, and Soroman — the widest of them — sits fourth of
  seven, dead centre of the desktop row.
*/
const TRUST_BRANDS: { name: string; logo?: string; h?: number }[] = [
  { name: "Orgone Tecnergy", logo: "/brands/orgone.png", h: 40 },
  { name: "Waste 360 Environmental Services", logo: "/brands/waste-360.png", h: 46 },
  { name: "Soroman", logo: "/brands/soroman.png", h: 30 },
  { name: "The Project School", logo: "/brands/the-project-school.png", h: 48 },
  { name: "COHATA — Coach Halimah Transformational Academy", logo: "/brands/cohata.png", h: 48 },
  { name: "Masa Treat", logo: "/brands/masa-treat.png", h: 58 },
  { name: "Lapidoth Educational Foundation", logo: "/brands/lapidoth.png", h: 46 },
]

type Brand = (typeof TRUST_BRANDS)[number]

function BrandMark({ brand: b }: { brand: Brand }) {
  if (!b.logo) {
    return (
      <span className="text-center text-[15px] font-bold tracking-tight text-neutral-500 transition-colors duration-300 hover:text-ink">
        {b.name}
      </span>
    )
  }
  return (
    <img
      src={b.logo}
      alt={b.name}
      loading="lazy"
      decoding="async"
      /* Height, not max-height: the point is that each mark gets its own. */
      style={{ height: b.h ?? 40 }}
      /*
        Faint grey, with the real colours on hover. `grayscale` rather than
        `brightness-0`: flattening to a single tone turns any mark with light
        shapes inside dark ones — The Project School's shield, Orgone's sphere
        — into a silhouette. Keeping luminance keeps that detail, and the low
        opacity is what makes the row quiet.
      */
      className="w-auto max-w-full object-contain opacity-55 grayscale transition-all duration-300 hover:opacity-100 hover:grayscale-0"
    />
  )
}

function Trust() {
  /* The track carries the logos twice; see the marquee keyframes in index.css
     for why the second copy is what makes the loop seamless. */
  const marqueeTrack = [...TRUST_BRANDS, ...TRUST_BRANDS]

  return (
    <section className="border-b border-neutral-200 bg-paper">
      <div className="mx-auto max-w-[1280px] px-6 py-14 lg:px-10 lg:py-16">
        <Reveal>
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-neutral-500 lg:text-center">
            Organisations we&rsquo;ve built for
          </p>
        </Reveal>

        <Reveal delay={100}>
          {/*
            Anything narrower than a desktop gets a marquee: seven logos across
            a phone or a tablet is either several cramped rows or a stranded
            orphan. It bleeds past the page gutter and fades at both edges, so
            the motion reads as a continuous band rather than logos bumping
            into a hard margin.

            Where the visitor has asked for less motion the animation stops and
            the strip becomes an ordinary swipeable row — with the duplicate
            copy hidden, since nothing is scrolling past to hide the seam.
          */}
          <div className="relative -mx-6 mt-10 overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_9%,black_91%,transparent)] motion-reduce:overflow-x-auto lg:hidden">
            <ul className="flex w-max animate-[marquee_26s_linear_infinite] items-center motion-reduce:animate-none">
              {marqueeTrack.map((b, i) => {
                const duplicate = i >= TRUST_BRANDS.length
                return (
                  <li
                    key={`${b.name}-${i}`}
                    aria-hidden={duplicate || undefined}
                    className={`mr-10 flex h-16 w-[150px] shrink-0 items-center justify-center ${
                      duplicate ? "motion-reduce:hidden" : ""
                    }`}
                  >
                    <BrandMark brand={b} />
                  </li>
                )
              })}
            </ul>
          </div>

          {/*
            Desktop: all seven on one line, always.

            `flex-nowrap` with shrinkable cells rather than a wrapping row,
            because wrapping is what produced the stray second row: the logos
            need about 1160px side by side, so anything between the lg
            breakpoint and that width tipped the last one or two onto their own
            line. Now the row holds, and when space runs short `max-w-full` on
            the image lets each mark scale down inside its cell instead.
          */}
          <ul className="mt-10 hidden flex-nowrap items-center justify-between gap-x-6 lg:flex xl:gap-x-8">
            {TRUST_BRANDS.map((b) => (
              <li
                key={b.name}
                className="flex h-16 min-w-0 flex-1 items-center justify-center"
              >
                <BrandMark brand={b} />
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </section>
  )
}

/*
  Who we are — the short version; the full story is the About page.

  Composed rather than stacked. The portrait is the anchor: an outlined frame
  steps out from behind it and a signed credit sits on it, so the right column
  reads as depth rather than as a rectangle placed beside some text. The left
  column earns its height through typographic hierarchy — a two-tone headline,
  a lead paragraph in ink, supporting copy in grey, and the studio's line set
  against an accent rule — rather than through more words.
*/
function AboutBrief() {
  return (
    <Section tone="mist">
      <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-12 lg:gap-20">
        <div className="lg:col-span-6">
          <Reveal>
            <EyebrowLabel>Who we are</EyebrowLabel>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="mt-6 text-[clamp(2.1rem,3.8vw,3.1rem)] font-black leading-[1.04] tracking-[-0.035em]">
              A software engineering company built around real operations.
            </h2>
          </Reveal>
          <Reveal delay={140}>
            <p className="mt-7 text-[17.5px] leading-[1.7] text-ink">
              Sableboxx is an independent software and digital infrastructure company based in
              Abuja, Nigeria. We build the systems organisations run on: the platforms that take
              an order from placement to delivery, reconcile the money and report to management.
              We start by understanding how the work actually happens, then engineer a system
              that fits it and stay on to maintain it. The people you meet are the people
              who do the work, so there are fewer layers between your problem and its solution.
            </p>
          </Reveal>
          
          <Reveal delay={260}>
            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4">
              <ButtonDark to="/about">More about Sableboxx</ButtonDark>
              {/* <TextLink to="/work">See the work</TextLink> */}
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-5 lg:col-start-8">
          <Reveal delay={160}>
            <div className="relative">
              {/*
                An outlined frame stepped off the portrait. Decorative, so it is
                hidden from assistive tech, and it sits behind the image rather
                than over it — on a small screen the offset is dropped, where
                there is no room to spare for it.
              */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 hidden rounded-3xl border border-ink/15 lg:block lg:translate-x-5 lg:translate-y-5"
              />
              <Img
                src={IMG.homeAbout}
                alt="The Sableboxx team at work"
                className="relative aspect-[4/5] rounded-3xl shadow-[0_40px_80px_-50px_rgba(11,16,23,0.55)] ring-1 ring-ink/5"
              />
              {/* Solid, not a scrim over the photo: the credit stays legible
                  even on the stand-in panel when the image cannot load. */}
              <div className="absolute bottom-5 left-5 max-w-[calc(100%-2.5rem)] rounded-2xl bg-ink/95 px-5 py-4">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-accent-bright">
                  Better systems. Smarter workflows.
                </p>
                <p className="mt-1.5 text-[16px] font-bold tracking-[-0.01em] text-white">
                  Helping organisations operate better.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </Section>
  )
}

/* Services. A grid of mixed tones and widths, each card showing the kind of
   system it describes. */
const SERVICE_CARDS: Record<
  string,
  { visual: React.ReactNode; span: string; tone: CardTone; flush?: boolean }
> = {
  "custom-software": {
    visual: <SoftwareVisual />,
    span: "lg:col-span-7",
    tone: "ink",
    flush: true,
  },
  "digital-transformation": {
    visual: <WorkflowVisual />,
    span: "lg:col-span-5",
    tone: "accent",
  },
  "government-institutional": {
    visual: <TrackerVisual />,
    span: "lg:col-span-5",
    tone: "mist",
  },
  "maintenance-support": {
    visual: <MonitorVisual />,
    span: "lg:col-span-7",
    tone: "glow",
    flush: true,
  },
}

function Services() {
  return (
    <Section id="services" tone="paper">
      <SectionHeader
        eyebrow="What we do"
        title="Four ways we work with organisations."
        intro="Each engagement is scoped around a result you can measure, agreed before any work begins."
      />
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
        {SERVICES.map((s, i) => {
          const c = SERVICE_CARDS[s.slug]!
          return (
            <Reveal key={s.slug} delay={(i % 2) * 90} className={c.span}>
              <BentoCard
                tone={c.tone}
                n={s.n}
                meta={s.timeline}
                title={s.t}
                text={s.d}
                tags={s.items.slice(0, 4)}
                link={{ to: s.to, label: s.cta }}
                visual={c.visual}
                flush={c.flush}
              />
            </Reveal>
          )
        })}
      </div>
      <Reveal>
        <div className="mt-5 flex flex-col gap-4 rounded-3xl border border-neutral-200 p-7 lg:flex-row lg:items-center lg:justify-between lg:px-9">
          <div className="max-w-2xl">
            <h3 className="text-[12px] font-semibold uppercase tracking-[0.18em] text-accent">
              Software products
            </h3>
            <p className="mt-2 text-[16px] leading-[1.7] text-neutral-700">
              Alongside client work, we develop proprietary platforms, offered
              by SaaS subscription or licence.
            </p>
          </div>
          <div className="shrink-0">
            <TextLink to="/contact">Ask what&rsquo;s available</TextLink>
          </div>
        </div>
      </Reveal>
    </Section>
  )
}

/* Why Choose Sableboxx */
const PRINCIPLES = [
  {
    t: "Value before code",
    d: "We agree what the work should save or earn before we write any code. That decides what gets built.",
  },
  {
    t: "Made for the people using it",
    d: "An internal tool deserves the same care as a customer app. If your team has to work around it, it isn't finished.",
  },
  {
    t: "The right tool for the job",
    d: "We use AI where it pays for itself and plain software where that's the better answer. The problem decides, not the trend.",
  },
  {
    t: "Built to grow with you",
    d: "Your organisation will change. What we build should adapt with it rather than need replacing in two years.",
  },
]

function Approach() {
  return (
    <Section tone="ink">
      <SectionHeader
        light
        eyebrow="Why Choose Sableboxx"
        title="We don't build technology for its own sake"
        intro="Engineering, automation and interface design sit in one team here. So the system fits the operation, the interface fits the people using it, and every project is judged on what it returns."
      />
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
        {PRINCIPLES.map((p, i) => {
          const c = PRINCIPLE_CARDS[i]!
          return (
            <Reveal key={p.t} delay={(i % 2) * 90} className={c.span}>
              <BentoCard
                /* An ink card would vanish on this ink section. */
                tone={c.tone === "ink" ? "smoke" : c.tone}
                title={p.t}
                text={p.d}
                visual={<c.Visual />}
              />
            </Reveal>
          )
        })}
      </div>
    </Section>
  )
}

/* Selected work */
/*
  What the reference system covers. This panel names the client, so it only
  appears once the Soroman case study is published in the dashboard.
*/
const REFERENCE_MODULES = [
  "Multi-location operations",
  "Order and PFI management",
  "Financial reconciliation",
  "Truck ticket and security exit workflows",
  "Fleet and delivery management",
  "Role-based administration",
  "Automated reporting",
  "SMS and WhatsApp integrations",
]

function Work() {
  const { items } = useCollection("caseStudies")
  const published = items.filter((w) => w.status === "Published")
  const reference = published.find((w) => w.slug === "soroman-energy")
  const works = published.filter((w) => w !== reference).slice(0, 4)
  if (published.length === 0) return null

  return (
    <Section id="work" tone="paper">
      <SectionHeader
        layout="centered"
        eyebrow="Selected work"
        title="Systems we’ve designed, built and supported."
        intro="Our work includes enterprise software, business platforms and digital presence for organisations in Nigeria and beyond."
      />
      {reference && (
        <Reveal>
          <div className="mb-6 grid grid-cols-1 gap-8 rounded-3xl bg-ink p-8 text-white lg:grid-cols-12 lg:gap-12 lg:p-12">
            <div className="lg:col-span-5">
              <p className="text-[12px] font-semibold uppercase tracking-[0.18em] text-accent-bright">
                Enterprise reference
              </p>
              <h3 className="mt-4 text-[clamp(1.8rem,3vw,2.5rem)] font-black leading-[1.08] tracking-[-0.03em]">
                {reference.client}
              </h3>
              <p className="mt-4 text-[16px] leading-[1.7] text-neutral-300">
                {reference.summary}
              </p>
              <div className="mt-7">
                <ButtonLight to={`/work/${reference.slug}`}>
                  Read the case study
                </ButtonLight>
              </div>
            </div>
            <ul className="grid grid-cols-1 content-center gap-x-8 gap-y-4 sm:grid-cols-2 lg:col-span-7">
              {REFERENCE_MODULES.map((m) => (
                <li
                  key={m}
                  className="flex items-start gap-3 text-[15px] leading-snug text-neutral-200"
                >
                  <Icon
                    name="check"
                    className="mt-0.5 h-4 w-4 shrink-0 text-accent-bright"
                  />
                  {m}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      )}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        {works.map((w, i) => (
          <Reveal key={w.id} delay={(i % 2) * 90}>
            <CaseCard work={w} />
          </Reveal>
        ))}
      </div>
      {/* Below the cards, not beside the heading: the natural next step after
          reading them, rather than an invitation to skip them. */}
      <Reveal>
        <div className="mt-12 flex lg:mt-14 lg:justify-center">
          <ButtonDark to="/work">View all work</ButtonDark>
        </div>
      </Reveal>
    </Section>
  )
}

/* Who we work with */
const AUDIENCES = [
  {
    icon: "rising" as const,
    k: "Growing businesses",
    t: "You've outgrown the tools holding you together.",
    d: "We build the systems and automation your next stage actually needs.",
  },
  {
    icon: "building" as const,
    k: "Established companies",
    t: "Your business has matured. Your systems should too.",
    d: "We replace manual workarounds with software that scales across teams and locations.",
  },
  {
    icon: "institution" as const,
    k: "Government agencies",
    t: "Public services need accountable systems.",
    d: "Clear workflows and reliable records for ministries, agencies and state governments.",
  },
  {
    icon: "startup" as const,
    k: "Institutions and foundations",
    t: "Complex organisations need clear systems.",
    d: "Structured platforms for schools, foundations and member bodies that make information easy to find and act on.",
  },
]

function Audiences() {
  return (
    <Section tone="mist">
      <SectionHeader
        eyebrow="Who we work with"
        title="We work with organisations that run on their systems."
        // intro="Whether you're starting from scratch, repositioning an established business or building something new, we start where you are and build for where you're going."
      />
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
        <Reveal className="lg:col-span-5">
          <div className="relative lg:sticky lg:top-28">
            <Img
              src={IMG.team}
              alt="Two members of the Sableboxx team reviewing work on a laptop"
              className="aspect-[4/5] rounded-2xl"
            />
            <div className="absolute bottom-5 left-5 right-5 rounded-xl bg-ink/85 px-5 py-4 text-white backdrop-blur">
              <div className="text-[15px] font-bold">
                One team, requirements to support
              </div>
              <div className="mt-0.5 text-[13px] text-neutral-400">
                The people you meet are the people who do the work.
              </div>
            </div>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 gap-4 lg:col-span-7">
          {AUDIENCES.map((a, i) => (
            <Reveal key={a.k} delay={i * 80}>
              <div className="group flex h-full items-start gap-5 rounded-2xl border border-neutral-200 bg-white p-7 transition-all duration-500 hover:-translate-y-1 hover:border-transparent hover:bg-ink hover:shadow-[0_24px_50px_-30px_rgba(11,16,23,0.5)]">
                <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-mist text-ink transition-colors group-hover:bg-accent group-hover:text-white">
                  <Icon name={a.icon} className="h-6 w-6" />
                </span>
                <div>
                  <span className="text-[11.5px] font-semibold uppercase tracking-[0.16em] text-accent transition-colors group-hover:text-accent-bright">
                    {a.k}
                  </span>
                  <h3 className="mt-2 text-[18px] font-bold leading-snug tracking-[-0.01em] transition-colors group-hover:text-white">
                    {a.t}
                  </h3>
                  <p className="mt-1.5 text-[14.5px] leading-relaxed text-neutral-600 transition-colors group-hover:text-neutral-300">
                    {a.d}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </Section>
  )
}

/* Testimonials */
const QUOTES = [
  {
    q: "Sableboxx understood what we were trying to achieve and turned it into something far better than we imagined.",
    n: "Ada Okonkwo",
    r: "Founder, Creators Aberdeen",
  },
  {
    q: "They asked the questions no one else did. The result finally looks like the company we actually are.",
    n: "Michael Reeves",
    r: "Director, Lapidoth Foundation",
  },
  {
    q: "Strategy first, then design. It changed how customers see us, and how many of them stay.",
    n: "Zainab Bello",
    r: "CEO, Siren Global",
  },
]

function Testimonials() {
  return null
  // return (
  //   <Section tone="paper">
  //     <SectionHeader
  //       eyebrow="Client words"
  //       layout="centered"
  //       title="Trusted by the people we work with."
  //       intro="A few words from clients and partners we’ve had the opportunity to work with."
  //     />
  //     <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
  //       {QUOTES.map((t, i) => (
  //         <Reveal key={t.n} delay={i * 90}>
  //           <figure className="flex h-full flex-col rounded-2xl border border-neutral-200 bg-white p-8">
  //             <Icon name="quote" className="h-8 w-8 text-accent" />
  //             <blockquote className="mt-5 flex-1 text-[17px] leading-[1.6] text-ink">
  //               {t.q}
  //             </blockquote>
  //             <figcaption className="mt-8 border-t border-neutral-200 pt-5">
  //               <div className="text-[15px] font-bold">{t.n}</div>
  //               <div className="mt-0.5 text-[13.5px] text-neutral-500">
  //                 {t.r}
  //               </div>
  //             </figcaption>
  //           </figure>
  //         </Reveal>
  //       ))}
  //     </div>
  //   </Section>
  // )
}

/* In numbers */
function Results() {
  return (
    <Section tone="ink">
      {/* <SectionHeader
        light
        eyebrow="In numbers"
        title="The work so far"
        intro="Good work looks good. Great work moves a number your business cares about."
      /> */}
      <div className="grid grid-cols-2 gap-x-6 gap-y-12 lg:grid-cols-4">
        {STUDIO_STATS.map((r, i) => (
          <Reveal key={r.l} delay={i * 80}>
            <div className="border-l border-white/15 pl-6">
              <div className="text-[clamp(2.8rem,4.6vw,4rem)] font-black leading-none tracking-[-0.03em]">
                <CountUp value={r.n} />
              </div>
              <div className="mt-3 text-[14px] text-neutral-400">{r.l}</div>
            </div>
          </Reveal>
        ))}
      </div>
    </Section>
  )
}

/* Process */
function ProcessSection() {
  return null
  // return (
  //   <Section id="process" tone="paper">
  //     <SectionHeader
  //       eyebrow="How we work"
  //       title="A clear path from idea to launch"
  //       intro="Six stages, agreed up front, so you always know what's happening, what comes next and what it costs."
  //       action={<TextLink to="/process">See the full process</TextLink>}
  //     />
  //     <div className="grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-neutral-200 bg-neutral-200 sm:grid-cols-2 lg:grid-cols-3">
  //       {PROCESS.map((p, i) => (
  //         <Reveal key={p.n} delay={(i % 3) * 90} className="bg-white">
  //           <div className="group h-full bg-white p-8 transition-colors duration-300 hover:bg-ink">
  //             <span className="text-[13px] font-black tracking-widest text-accent transition-colors group-hover:text-accent-bright">
  //               {p.n}
  //             </span>
  //             <h3 className="mt-4 text-[22px] font-bold tracking-[-0.02em] transition-colors group-hover:text-white">
  //               {p.t}
  //             </h3>
  //             <p className="mt-3 text-[15px] leading-[1.7] text-neutral-600 transition-colors group-hover:text-neutral-400">
  //               {p.d}
  //             </p>
  //             <p className="mt-5 text-[14px] font-semibold text-ink transition-colors group-hover:text-white">
  //               {p.tag}
  //             </p>
  //           </div>
  //         </Reveal>
  //       ))}
  //     </div>
  //   </Section>
  // )
}

/* FAQ */
const FAQS = [
  {
    q: "How much does a project cost?",
    a: "It depends on the scope, and we'd rather not guess. After a short conversation about what you need, we send a written proposal with the price, the timeline and exactly what's included.",
  },
  {
    q: "How long does a project take?",
    a: "Automation projects usually take 4 to 12 weeks, and a first release of custom software 8 to 16. Larger platforms are delivered in phases. You'll have a firm timeline in writing before any work starts.",
  },
  {
    q: "Do you work with government agencies?",
    a: "Yes, it's a growing part of our focus. Our largest reference system so far is enterprise software in the private sector, and the same engineering applies to public services. We can respond to a procurement directly or partner with a prime contractor.",
  },
  {
    q: "Can you take over or connect to an existing system?",
    a: "Yes. We start by reviewing what you have, then tell you honestly whether to maintain it, integrate with it or replace it.",
  },
  {
    q: "Do you work with organisations outside Nigeria?",
    a: "Yes. We're based in Abuja and work with clients in Nigeria and abroad.",
  },
  {
    q: "What kind of work can you automate?",
    a: "Anything repetitive and rule-based: moving data between tools, processing documents and invoices, first-line customer support, routine reporting. We start by finding where your team's hours actually go and automate the biggest drain first, so you see the value early.",
  },
  {
    q: "Do we actually need AI?",
    a: "Often you don't. Plenty of problems people want solved with AI are handled better and more cheaply by ordinary software or a cleaner process. We'll tell you when that's the case. Building AI you don't need is the quickest way to waste a budget.",
  },
  {
    q: "Do you provide support after launch?",
    a: "Yes. Our monthly support covers maintenance, security updates, performance and new features as your needs change.",
  },
  {
    q: "Do you still build websites?",
    a: "Yes, particularly where a website is part of a wider system or digital presence. It's no longer the main focus of our work, but we're happy to discuss what you need.",
  },
]

function Faq() {
  const [open, setOpen] = useState<number | null>(0)
  return (
    <Section tone="mist">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12 lg:gap-16">
        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-28">
            <SectionHeader
              layout="stacked"
              eyebrow="Questions"
              title="What people usually ask us"
              intro={
                <>
                  Can&rsquo;t see your question? Email{" "}
                  <a
                    href={`mailto:${SITE.email}`}
                    className="font-medium text-ink underline underline-offset-4"
                  >
                    {SITE.email}
                  </a>{" "}
                  and we&rsquo;ll answer it directly.
                </>
              }
            />
          </div>
        </div>

        <div className="lg:col-span-8">
          <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white">
            {FAQS.map((f, i) => {
              const isOpen = open === i
              return (
                <div
                  key={f.q}
                  className="border-b border-neutral-200 last:border-b-0"
                >
                  <button
                    onClick={() => setOpen(isOpen ? null : i)}
                    aria-expanded={isOpen}
                    className="flex w-full items-center justify-between gap-6 px-6 py-6 text-left lg:px-8"
                  >
                    <span className="text-[17px] font-bold tracking-[-0.01em]">
                      {f.q}
                    </span>
                    <span
                      className={`grid h-8 w-8 shrink-0 place-items-center rounded-full border transition-all duration-300 ${
                        isOpen
                          ? "rotate-45 border-ink bg-ink text-white"
                          : "border-neutral-200 text-ink"
                      }`}
                    >
                      <Icon name="plus" className="h-4 w-4" />
                    </span>
                  </button>
                  <div
                    className="grid transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]"
                    style={{
                      gridTemplateRows: isOpen ? "1fr" : "0fr",
                      opacity: isOpen ? 1 : 0,
                    }}
                  >
                    <div className="overflow-hidden">
                      <p className="max-w-2xl px-6 pb-7 text-[15.5px] leading-[1.7] text-neutral-600 lg:px-8">
                        {f.a}
                      </p>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </Section>
  )
}

/* Insights. Hidden until there is something to show: a heading over an empty
   grid reads as a broken page. */
function Insights() {
  const { items } = useCollection("posts")
  const posts = items.filter((p) => p.status === "Published").slice(0, 3)
  if (posts.length === 0) return null

  // return (
  //   <Section id="insights" tone="paper">
  //     <SectionHeader
  //       eyebrow="Insights"
  //       layout="stacked"
  //       title="Notes on building better businesses"
  //       intro="Practical thinking on software, automation and brand, written for the people making the decisions."
  //       action={<TextLink to="/insights">All articles</TextLink>}
  //     />
  //     <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
  //       {posts.map((a, i) => (
  //         <Reveal key={a.id} delay={i * 90}>
  //           <PostCard post={a} />
  //         </Reveal>
  //       ))}
  //     </div>
  //   </Section>
  // )
}

export default function Home() {
  return (
    <>
      <Seo
        title="Sableboxx"
        description="Sableboxx is a software and digital infrastructure company in Abuja. We design, build and implement digital systems that help businesses and institutions operate better."
        jsonLd={[
          organizationSchema(),
          websiteSchema(),
          faqSchema(FAQS),
          ...SERVICES.map(serviceSchema),
        ]}
      />
      <Hero />
      <Trust />
      <AboutBrief />
      <Services />
      <ImageBand
        tone="paper"
        src={IMG.homeBand}
        alt="The Sableboxx team working through a project together"
        eyebrow="One team"
        caption="Engineering, automation and support, working as one team"
      />
      <Approach />
      <Work />
      <Audiences />
      <Testimonials />
      <Results />
      <ProcessSection />
      <Faq />
      <Insights />
      <FinalCta />
    </>
  )
}
